import React, { useMemo } from 'react';
import styled from 'styled-components';
import { ReportAPIResponse } from '../api';
import { useHistory } from 'react-router-dom';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { stageLookup } from '../../Reports/utils';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { Program } from '../../Reports/NewReportDialog';
import { TrueImpactTransparentButton } from 'shared/design-system/components/TrueImpactButton';
import {
  faPen,
  faClone,
  faShieldKeyhole,
} from '@fortawesome/pro-light-svg-icons';
import { getProgramNameUrl } from '../../ReportBuilder/Components/Budget/utils';

interface ReportTableProps {
  program: Program;
  reports: ReportAPIResponse[];
  isAdminUser: boolean;
  handleAdminModalOpen: (r: ReportAPIResponse) => unknown;
  handleAdminModalClose: () => unknown;
  cloneExistingYear: (year: number) => unknown;
}

const StyledDiv = styled.div`
  border-radius: 4px;
  border: 1px solid ${COLORS.grayLight};
  background-color: ${COLORS.whiteBase};
  .report-row,
  .report-header {
    display: flex;
    padding-left: ${SPACERS.medium};
    padding-right: ${SPACERS.medium};
    gap: ${SPACERS.medium};
  }
  .report-header {
    font-weight: bold;
    padding-top: ${SPACERS.medium};
    padding-bottom: ${SPACERS.medium};
    background-color: ${COLORS.grayLight};
  }
  .report-row {
    padding-top: ${SPACERS.small};
    padding-bottom: ${SPACERS.small};
  }
  .report-row:nth-child(odd) {
    background-color: ${COLORS.grayLightest};
  }
  .year-column {
    width: 36px;
  }
  .contribution-column {
    width: 107px;
    text-align: center;
  }
  .stage-column {
    width: 100px;
  }
  .status-column {
    width: 80px;
  }
  .total-column {
    width: 82px;
    text-align: center;
  }
  .actions-column {
    flex: 1;
    display: flex;
    gap: ${SPACERS.medium};
    justify-content: flex-end;
  }
`;

const ReportTable: React.FC<ReportTableProps> = ({
  reports,
  program,
  isAdminUser,
  handleAdminModalOpen,
  cloneExistingYear,
}) => {
  const { push } = useHistory();

  const reportsArray = useMemo(
    () => reports.sort((x, y) => Number(y.year) - Number(x.year)),
    [reports],
  );

  return (
    <StyledDiv>
      <div className="report-header">
        <div className="year-column">Year</div>
        <div className="contribution-column">Contributions</div>
        <div className="stage-column">Stage</div>
        <div className="status-column">Status</div>
        <div className="total-column">Donations</div>
        <div className="actions-column"></div>
      </div>
      {reportsArray.map(r => {
        const onClick = () => {
          push(
            `/npo/report-builder/${getProgramNameUrl(program.name)}/${r.dbId}`,
          );
        };
        const onClone = () => {
          const year = Number(r.year);
          year && cloneExistingYear(year);
        };
        const onAdmin = () => handleAdminModalOpen(r);
        return (
          <div key={r.year} className="report-row">
            <div className="year-column">{r.year}</div>
            <div className="contribution-column">{r.numAllocations || 0}</div>
            <div className="stage-column">
              {stageLookup[r.stage] || r.stage}
            </div>
            <div className="status-column">{r.status}</div>
            <div className="total-column">
              ${numberWithCommas(r.totalDonations)}
            </div>
            <div className="actions-column">
              <TrueImpactTransparentButton
                isText={true}
                icon={faPen}
                onClick={onClick}
              >
                Edit
              </TrueImpactTransparentButton>
              <TrueImpactTransparentButton
                isText={true}
                icon={faClone}
                onClick={onClone}
              >
                Clone
              </TrueImpactTransparentButton>
              {isAdminUser && (
                <TrueImpactTransparentButton
                  isText={true}
                  icon={faShieldKeyhole}
                  onClick={onAdmin}
                >
                  Admin
                </TrueImpactTransparentButton>
              )}
            </div>
          </div>
        );
      })}
    </StyledDiv>
  );
};

export default ReportTable;
